import { CollectionName } from '@/shared/lib/sj-orm/constants';

import { SecureJsonBase } from '../../secure-json/core/secure-json-base';
import { generateDtoId } from '../helpers';
import { CollectionOperation } from '../stores/secureJsonCollections.store';
import { CollectionsSjMap, CollectionToSjMap } from '../types';

import { SJCollection } from './SJCollection';

export * from './SJCollection';

export class SJDatabase {
  collections: Partial<CollectionToSjMap> = {};

  constructor(
    readonly colMap: CollectionsSjMap,
    readonly isDbReady: boolean,
    readonly awaitInited: Promise<boolean>,
    onChangeColItem: <T>(
      name: CollectionName,
      secureJsonBase: SecureJsonBase<T>,
      operation: CollectionOperation,
    ) => void,
  ) {
    const assetCollection = colMap.get(CollectionName.ASSETS);
    const myHubCollection = colMap.get(CollectionName.MY_HUB);

    const encryptedFileKeyCollection = colMap.get(CollectionName.ENCRYPTED_FILE_KEY);
    const beneficiariesCollection = colMap.get(CollectionName.BENEFICIARIES);
    const delegatingRequestsCollection = colMap.get(CollectionName.DELEGATING_REQUESTS);
    const beneficiaryPersonalDataDocumentsCollection = colMap.get(
      CollectionName.BENEFICIARY_PERSONAL_DATA_DOCUMENT,
    );
    const assetInformationDocumentCollection = colMap.get(
      CollectionName.ASSET_INFORMATION_DOCUMENT,
    );
    const sowe = colMap.get(CollectionName.SOWE);
    const soweAttachmentCollection = colMap.get(CollectionName.SOWE_ATTACHMENTS);
    const assistantMnemonicCollection = colMap.get(CollectionName.ASSISTANT_MNEMONIC);
    const contactsCollection = colMap.get(CollectionName.CONTACTS);
    const privateDocumentsCollection = colMap.get(CollectionName.PRIVATE_DOCUMENTS);
    const personalIdentifiersDocumentsCollection = colMap.get(
      CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    );
    const fileMetaInfosCollection = colMap.get(CollectionName.FILE_META_INFO);
    const notificationCollection = colMap.get(CollectionName.NOTIFICATION);
    const scenarioMetaInfosCollection = colMap.get(CollectionName.SCENARIO_META_INFO);
    const inboxCollection = colMap.get(CollectionName.INBOX);
    const interwills = colMap.get(CollectionName.INTERWILLS);

    if (assetCollection) {
      this.collections.assets = new SJCollection(
        CollectionName.ASSETS,
        assetCollection,
        onChangeColItem,
      );
    }

    if (myHubCollection) {
      this.collections.myHub = new SJCollection(
        CollectionName.MY_HUB,
        myHubCollection,
        onChangeColItem,
      );
    }

    if (sowe) {
      this.collections.sowe = new SJCollection(CollectionName.SOWE, sowe, onChangeColItem);
    }

    if (soweAttachmentCollection) {
      this.collections.soweAttachments = new SJCollection(
        CollectionName.SOWE_ATTACHMENTS,
        soweAttachmentCollection,
        onChangeColItem,
      );
    }

    if (encryptedFileKeyCollection) {
      this.collections.encryptedFileKey = new SJCollection(
        CollectionName.ENCRYPTED_FILE_KEY,
        encryptedFileKeyCollection,
        onChangeColItem,
      );
    }
    if (beneficiariesCollection) {
      this.collections.beneficiaries = new SJCollection(
        CollectionName.BENEFICIARIES,
        beneficiariesCollection,
        onChangeColItem,
      );
    }
    if (delegatingRequestsCollection) {
      this.collections.delegatingRequests = new SJCollection(
        CollectionName.DELEGATING_REQUESTS,
        delegatingRequestsCollection,
        onChangeColItem,
      );
    }
    if (beneficiaryPersonalDataDocumentsCollection) {
      this.collections.beneficiaryPersonalDataDocuments = new SJCollection(
        CollectionName.BENEFICIARY_PERSONAL_DATA_DOCUMENT,
        beneficiaryPersonalDataDocumentsCollection,
        onChangeColItem,
      );
    }
    if (assetInformationDocumentCollection) {
      this.collections.assetInformationDocument = new SJCollection(
        CollectionName.ASSET_INFORMATION_DOCUMENT,
        assetInformationDocumentCollection,
        onChangeColItem,
      );
    }
    if (assistantMnemonicCollection) {
      this.collections.assistantMnemonic = new SJCollection(
        CollectionName.ASSISTANT_MNEMONIC,
        assistantMnemonicCollection,
        onChangeColItem,
      );
    }

    if (notificationCollection) {
      this.collections.notification = new SJCollection(
        CollectionName.NOTIFICATION,
        notificationCollection,
        onChangeColItem,
      );
    }

    if (contactsCollection) {
      this.collections.contacts = new SJCollection(
        CollectionName.CONTACTS,
        contactsCollection,
        onChangeColItem,
      );
    }
    if (privateDocumentsCollection) {
      this.collections.privateDocuments = new SJCollection(
        CollectionName.PRIVATE_DOCUMENTS,
        privateDocumentsCollection,
        onChangeColItem,
      );
    }
    if (personalIdentifiersDocumentsCollection) {
      this.collections.personalIdentifiersDocuments = new SJCollection(
        CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
        personalIdentifiersDocumentsCollection,
        onChangeColItem,
      );
    }
    if (fileMetaInfosCollection) {
      this.collections.fileMetaInfos = new SJCollection(
        CollectionName.FILE_META_INFO,
        fileMetaInfosCollection,
        onChangeColItem,
      );
    }
    if (scenarioMetaInfosCollection) {
      this.collections.scenarioMetaInfos = new SJCollection(
        CollectionName.SCENARIO_META_INFO,
        scenarioMetaInfosCollection,
        onChangeColItem,
      );
    }

    if (inboxCollection) {
      this.collections.inbox = new SJCollection(
        CollectionName.INBOX,
        inboxCollection,
        onChangeColItem,
      );
    }

    if (interwills) {
      this.collections.interwills = new SJCollection(
        CollectionName.INTERWILLS,
        interwills,
        onChangeColItem,
      );
    }

    // const userMigrationsCollection = store.collections.get(CollectionName.MIGRATIONS) as
    //   | SecureJsonBase<MigrationApplyResultDto>
    //   | undefined;

    // if (userMigrationsCollection) {
    //   this.collections.migrations = new SJCollection<MigrationApplyResultDto>(
    //     CollectionName.MIGRATIONS,
    //     userMigrationsCollection,
    //     onChangeColItem,
    //   );
    // }
  }

  getSize() {
    const allItems = Object.entries(this.collections)
      // .filter(([key]) => key !== CollectionName.MIGRATIONS)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(([_, value]) => value)
      .flat()
      .filter(Boolean);

    return allItems.reduce((acc, cur) => acc + (cur?.size() || 0), 0);
  }

  public generateId(prefix: CollectionName): string {
    return generateDtoId(prefix);
  }

  static generateId(prefix: CollectionName): string {
    return generateDtoId(prefix);
  }
}
